import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, CornerDownLeft } from 'lucide-react';
import { useOverlay } from './osPrimitives';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = {
  booking: 'Booking', client: 'Client', vehicle: 'Vehicle',
  partner: 'Partner', rental: 'Rental', condition_report: 'Report', command: 'Jump',
};

// Reuses the existing Unified Command backend (/api/admin/search). Does NOT rebuild search logic.
export default function OSCommand({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  useOverlay(open, onClose);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 60); }, [open]);
  useEffect(() => { if (!open) { setQ(''); setResults([]); } }, [open]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('ace_admin_token');
        const r = await fetch(`${API}/api/admin/search?q=${encodeURIComponent(q)}&limit=15`,
          { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const d = await r.json();
        if (!cancelled) setResults(d.results || []);
      } catch { if (!cancelled) setResults([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const go = useCallback((r) => {
    onClose();
    const dl = r.deep_link || {};
    let path = dl.route || r.route || r.url || '';
    path = path.replace(API, '');
    if (!path) return;
    if (dl.tab) path += (path.includes('?') ? '&' : '?') + `tab=${dl.tab}`;
    if (dl.record_id) path += (path.includes('?') ? '&' : '?') + `id=${dl.record_id}`;
    navigate(path);
  }, [navigate, onClose]);

  if (!open) return null;
  const grouped = results.reduce((acc, r) => { (acc[r.type] = acc[r.type] || []).push(r); return acc; }, {});

  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center pt-[8vh] px-4"
         style={{ background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(6px)' }}
         onClick={onClose} data-testid="os-command-overlay">
      <div className="os-elev w-full max-w-2xl rounded-2xl overflow-hidden os-animate" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--os-border)' }}>
          <Search className="h-5 w-5" style={{ color: 'var(--os-muted)' }} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="os-command-input"
                 placeholder="Search clients, bookings, vehicles, partners…  (Unified Command)"
                 className="flex-1 bg-transparent outline-none text-[15px]" style={{ color: 'var(--os-text)' }} />
          <button onClick={onClose} className="p-1 rounded-lg" style={{ color: 'var(--os-muted)' }} data-testid="os-command-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[52vh] overflow-y-auto os-scroll p-2">
          {loading && <p className="text-sm px-3 py-6 text-center" style={{ color: 'var(--os-muted)' }}>Searching…</p>}
          {!loading && q.length >= 2 && results.length === 0 && (
            <p className="text-sm px-3 py-6 text-center" style={{ color: 'var(--os-muted)' }}>No matches for “{q}”.</p>
          )}
          {!loading && q.length < 2 && (
            <p className="text-sm px-3 py-6 text-center" style={{ color: 'var(--os-muted)' }}>Type at least 2 characters. Results assemble canonical records — reads only.</p>
          )}
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type} className="mb-2">
              <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--os-muted)' }}>{TYPE_LABEL[type] || type}</p>
              {items.map((r, i) => (
                <button key={i} onClick={() => go(r)} data-testid={`os-result-${type}-${i}`}
                        className="w-full text-left px-3 py-2 rounded-lg flex items-center justify-between group"
                        style={{ color: 'var(--os-text)' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--os-soft)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <span className="min-w-0 flex items-baseline gap-2">
                    <span className="text-sm truncate">{r.title || r.label || r.name}</span>
                    {r.hint && <span className="text-[11px] truncate" style={{ color: 'var(--os-muted)' }}>{r.hint}</span>}
                  </span>
                  <CornerDownLeft className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 shrink-0" />
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
